import { useNavigate } from "react-router-dom";
import "./TermsandConditions.css";
import { FaArrowLeft } from "react-icons/fa";
import { useAuth } from "../auth/useAuth";

function TermsandConditions() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleBack = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="terms-wrapper">
      <div className="terms-card">
        <header className="terms-header">
          <FaArrowLeft className="back-icon" onClick={handleBack} />
          <h2 className="terms-title">MARSMATE LEGAL POLICIES</h2>
        </header>

        <div className="terms-section">
          <h3 className="section-title">Privacy Policy (v1.0)</h3>
          <p className="last-updated">Last Updated: May 29, 2026</p>
          <p>Welcome to MarsMate AI ("we", "us", "our"). We are committed to protecting your personal data and your privacy. This Privacy Policy describes how Marsmate Business Intelligence Private Limited collects, uses, stores, and protects your information when you access or use the MarsMate AI applications, website, services, chatbot, or marketplaces (collectively, the "Services").</p>
          
          <h4>1. Information We Collect</h4>
          <p>We collect personal data that you provide to us directly, data collected automatically, and data received from third-party integrations:</p>
          <ul>
            <li><strong>Account Information:</strong> Name, email address, mobile number, password hash, role designation (client or student), and profile pictures.</li>
            <li><strong>Google Authentication Data:</strong> Google ID, email address, name, and profile pictures when you register or authenticate using Google Sign-In.</li>
            <li><strong>AI Interaction Data:</strong> Chatbot logs, business descriptions, career goals, MBTI traits, and diagnostic inputs submitted in the Idea Validation and Career Mind Mapping forms.</li>
            <li><strong>Uploaded Content:</strong> Documents, forms, text, and business parameters uploaded for analysis or report generation.</li>
            <li><strong>Transaction and Payment Data:</strong> Invoice records, payment references, and payment status. Actual payment transactions are handled securely by Razorpay; we do not store full credit card numbers or raw bank credentials.</li>
            <li><strong>Delivery and Location Data:</strong> Real-time geolocation coordinates of delivery agents and delivery customers (captured via Socket.io relay) strictly to track and display active delivery orders in MarsMart.</li>
            <li><strong>Technical and Usage Data:</strong> IP address, browser type, user agent details, activity logs, page views, and cookies.</li>
          </ul>

          <h4>2. Legal Basis for Processing (GDPR Compliance)</h4>
          <p>If you are accessing our Services from the European Economic Area (EEA), our legal basis for collecting and using your personal data depends on the context:</p>
          <ul>
            <li><strong>Consent:</strong> You have given explicit consent to process your data by agreeing to this Privacy Policy during registration.</li>
            <li><strong>Performance of Contract:</strong> To provide the AI diagnostic features, reports, invoices, and delivery tracking services you request.</li>
            <li><strong>Legitimate Interests:</strong> To improve application performance, protect database integrity, detect fraud, and secure authentication flows.</li>
          </ul>

          <h4>3. Cookies and Tracking Technologies</h4>
          <p>We use cookies to analyze web traffic, store session states, and customize layouts. You can control or decline cookies through your browser settings, though some features (such as remaining logged in) may not function correctly without them.</p>

          <h4>4. Data Sharing and Integrations</h4>
          <p>We do not sell, rent, or trade your personal data. We share data only with third-party service providers (Google OAuth for authentication and Razorpay for payment verification) and legal authorities when required by law or government regulation.</p>

          <h4>5. Data Retention and Deletion</h4>
          <p>We retain your personal data as long as your account remains active. You may request data deletion or account closure by contacting us at <strong>info@marsmateai.com</strong>. Upon verification, we will permanently delete or anonymize your account records, forms, and AI logs within 30 days, except where retention is required for tax, compliance, or legal obligations.</p>

          <h4>6. Security Practices</h4>
          <p>We implement industry-standard administrative, physical, and technical safeguards (such as TLS encryption, bcrypt password hashing, and tokenized JWT authentication cookies) to prevent unauthorized access, alteration, or disclosure of user data.</p>

          <h4>7. Contact Us</h4>
          <p>For privacy concerns, data subject access requests, or policy questions, please contact our Data Protection Officer at:</p>
          <p><strong>Address:</strong> HNO: 8-2-293, Plot No: 26, 2nd Floor, Srinagar Colony, Banjara Hills, Hyderabad, Telangana, 500073.<br />
          <strong>Email:</strong> info@marsmateai.com<br />
          <strong>Phone:</strong> +91 9494075914</p>
        </div>

        <div className="terms-section">
          <h3 className="section-title">Terms & Conditions (v1.0)</h3>
          <p className="last-updated">Last Updated: May 29, 2026</p>
          <p>These Terms & Conditions ("Terms") govern your access to and use of the MarsMate AI applications, chatbots, dashboards, and e-commerce portals operated by Marsmate Business Intelligence Private Limited ("Company", "we", "us", "our"). By registering an account, checking the consent box, or utilizing our Services, you agree to be bound by these Terms.</p>

          <h4>1. User Responsibilities & Account Security</h4>
          <ul>
            <li><strong>Eligibility:</strong> You must provide accurate, current, and complete registration information (name, email, phone, and role).</li>
            <li><strong>Credential Protection:</strong> You are solely responsible for protecting your account passwords and Google authentication sessions. Any action taken under your credentials will be deemed your action.</li>
            <li><strong>Role Specificity:</strong> Client accounts are for business idea validations and diagnostics. Student accounts are designated for career mind mapping. Cross-use or abuse of system credentials is prohibited.</li>
          </ul>

          <h4>2. Acceptable Use Policy</h4>
          <p>You agree not to engage in prohibited activities, including:</p>
          <ul>
            <li>Submitting false, fraudulent, or malformed URL/LinkedIn links to circumvent validation rules.</li>
            <li>Reverse-engineering the AI engines, scraping dashboards, or extracting diagnostic data.</li>
            <li>Interfering with delivery tracking updates or Socket.io coordinate relays on MarsMart.</li>
            <li>Using the Services to upload or transmit malware, spam, or defamatory material.</li>
          </ul>

          <h4>3. AI-Generated Content & Reports</h4>
          <ul>
            <li><strong>Diagnostic Reports:</strong> MarsMate AI provides automated analysis (Report A, Report B, and Scorecards) based on rule-based point systems and AI diagnostics.</li>
            <li><strong>No Professional Advice:</strong> AI-generated reports, scoring, and consultant matching are for informational and planning purposes only. They do not constitute certified legal, financial, or investment advice.</li>
            <li><strong>Reliance on Content:</strong> The Company is not liable for business decisions, losses, or startup failures resulting from reliance on AI-generated data or advice.</li>
          </ul>

          <h4>4. Intellectual Property</h4>
          <ul>
            <li><strong>Ownership:</strong> All source code, application designs, database schemas, logo graphics, report templates, and scoring algorithms are the exclusive intellectual property of the Company or its licensors.</li>
            <li><strong>User Content License:</strong> You grant us a non-exclusive, worldwide, royalty-free license to process your inputs (idea descriptions, traits, and answers) solely to generate reports and operate the Services.</li>
          </ul>

          <h4>5. Services, Payments, and Disclaimers</h4>
          <ul>
            <li><strong>Pricing & Payments:</strong> Access to advanced reports is paid. Payments are securely processed through Razorpay.</li>
            <li><strong>Service Disclaimers:</strong> The Services are provided "as-is" and "as-available". We make no warranties regarding continuous uptime, server errors, or absolute accuracy of external validation metrics.</li>
            <li><strong>Limitation of Liability:</strong> In no event shall the Company be liable for indirect, incidental, special, or consequential damages (including loss of profits, data, or goodwill) arising out of your use of the Services.</li>
          </ul>

          <h4>6. Suspension and Termination</h4>
          <p>We reserve the right to suspend or terminate your account and access to the Services immediately, without prior notice, if you breach these Terms, violate acceptable use guidelines, or fail to settle required service fees.</p>

          <h4>7. Governing Law and Disputes</h4>
          <p>These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising out of these Terms or the Services shall be subject to the exclusive jurisdiction of the courts located in Hyderabad, Telangana, India.</p>
        </div>
      </div>
    </div>
  );
}

export default TermsandConditions;
